import { VoiceTextarea } from '../components/VoiceTextarea';
import { VoiceInput } from '../components/VoiceInput';
// src/modules/SafeguardingModule.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Shield, AlertTriangle, CheckCircle, XCircle, Clock, Users,
  FileText, Plus, Trash2, Edit, Save, X, Eye, EyeOff,
  Activity, Heart, Droplet, User, Phone, Mail, MapPin,
  FileCheck, FileWarning, FileX, FilePlus, FileMinus,
  Calendar, Lock, Unlock, Home, Building, Scale,
  Search, Filter, Flag, Bookmark, Book, Award, Crown
} from 'lucide-react';
import BodyMap, { BodyPart, Annotation } from './BodyMap';

// ─── TYPE DEFINITIONS ──────────────────────────────────────
interface SafeguardingAlertRecord {
  id: string;
  resident_id: string;
  resident_name: string;
  type_of_alleged_abuse: 'Physical' | 'Sexual' | 'Psychological' | 'Financial' | 'Neglect' | 'Discriminatory' | 'Institutional';
  source_of_alert: 'Staff Report' | 'Family Complaint' | 'Whistleblower' | 'External Professional';
  supervising_council: 'Wakefield' | 'Leeds' | 'Bradford' | 'Kirklees';
  section_42_referral_status: 'Assessment Pending' | 'Section 42 Enquiry Opened' | 'Section 42 Enquiry Closed' | 'No Further Action (NFA)';
  local_authority_case_reference: string;
  assigned_social_worker_info: string;
  date_alert_raised_internally: string;
  cqc_notification_submitted_at: string | null;
  cqc_notification_reference_num: string | null;
  leeds_msp_voice_recorded: boolean;
  kirklees_gateway_notification_date: string | null;
  kirklees_panel_review_meeting_confirmed: boolean;
  dsl_lead_signature: string;
  registered_manager_signoff_at: string | null;
  outcome_summary: string;
  is_sealed_and_locked: boolean;
  bodyMapAnnotations?: Annotation[];
}

// ─── DEFAULT DATA SEED ────────────────────────────────────
const DEFAULT_ALERTS: SafeguardingAlertRecord[] = [
  {
    id: '1',
    resident_id: 'R002',
    resident_name: 'John Williams',
    type_of_alleged_abuse: 'Neglect',
    source_of_alert: 'Staff Report',
    supervising_council: 'Wakefield',
    section_42_referral_status: 'Assessment Pending',
    local_authority_case_reference: '',
    assigned_social_worker_info: '',
    date_alert_raised_internally: new Date().toISOString(),
    cqc_notification_submitted_at: null,
    cqc_notification_reference_num: null,
    leeds_msp_voice_recorded: false,
    kirklees_gateway_notification_date: null,
    kirklees_panel_review_meeting_confirmed: false,
    dsl_lead_signature: '',
    registered_manager_signoff_at: null,
    outcome_summary: '',
    is_sealed_and_locked: false,
    bodyMapAnnotations: [],
  },
  {
    id: '2',
    resident_id: 'R005',
    resident_name: 'Emma Thompson',
    type_of_alleged_abuse: 'Physical',
    source_of_alert: 'Family Complaint',
    supervising_council: 'Leeds',
    section_42_referral_status: 'Section 42 Enquiry Opened',
    local_authority_case_reference: 'LEEDS-2024-007',
    assigned_social_worker_info: 'Sarah Patel (Leeds City Council)',
    date_alert_raised_internally: new Date(Date.now() - 12*60*60*1000).toISOString(),
    cqc_notification_submitted_at: null,
    cqc_notification_reference_num: null,
    leeds_msp_voice_recorded: false,
    kirklees_gateway_notification_date: null,
    kirklees_panel_review_meeting_confirmed: false,
    dsl_lead_signature: '',
    registered_manager_signoff_at: null,
    outcome_summary: '',
    is_sealed_and_locked: false,
    bodyMapAnnotations: [],
  }
];

// ─── COMPONENT ─────────────────────────────────────────────
export function SafeguardingModule({ user }: { user: any }) {
  // ─── ALL HOOKS – MUST BE UNCONDITIONAL ──────────────────
  const [alerts, setAlerts] = useState<SafeguardingAlertRecord[]>([]);
  const [selectedAlert, setSelectedAlert] = useState<SafeguardingAlertRecord | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<'records' | 'body-map'>('records');
  const [formData, setFormData] = useState<Partial<SafeguardingAlertRecord>>({});
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | 'warning' } | null>(null);
  const [filterCouncil, setFilterCouncil] = useState<string>('All');
  const [filterStatus, setFilterStatus] = useState<string>('All');
  const timerInterval = useRef<NodeJS.Timeout | null>(null);
  const [bodyMapGender, setBodyMapGender] = useState<'male' | 'female'>('male');
  const [bodyMapSide, setBodyMapSide] = useState<'front' | 'back' | 'left' | 'right'>('front');
  const [bodyMapSelectedParts, setBodyMapSelectedParts] = useState<BodyPart[]>([]);
  const [bodyMapAnnotations, setBodyMapAnnotations] = useState<Annotation[]>([]);

  // ─── EFFECTS ──────────────────────────────────────────────
  useEffect(() => {
    const stored = localStorage.getItem('rmacs_safeguarding_vault');
    if (stored) {
      try { setAlerts(JSON.parse(stored)); } catch { setAlerts(DEFAULT_ALERTS); }
    } else {
      setAlerts(DEFAULT_ALERTS);
      localStorage.setItem('rmacs_safeguarding_vault', JSON.stringify(DEFAULT_ALERTS));
    }

    timerInterval.current = setInterval(() => {
      setAlerts(prev => [...prev]);
    }, 60000);

    return () => {
      if (timerInterval.current) clearInterval(timerInterval.current);
    };
  }, []);

  // ─── RBAC CHECK (after all hooks) ────────────────────────
  const allowedRoles = ['SuperAdmin', 'HomeManager', 'ClinicalLead'];
  if (!user || !allowedRoles.includes(user.role)) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center bg-[#F7F5EE] rounded-2xl p-8">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center border-2 border-red-200">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-red-700">❌ Security Protection Alert</h2>
          <p className="text-gray-600 mt-2">Access Denied. Confidential Safeguarding Portals are restricted to Authorized Governance Leads.</p>
          <p className="text-sm text-gray-500 mt-1">Your role: {user?.role || 'Unknown'}</p>
        </div>
      </div>
    );
  }

  // ─── HELPERS & HANDLERS ──────────────────────────────────
  const getAbuseTypeIcon = (type: string) => {
    const map: any = {
      'Physical': '🔴',
      'Sexual': '🟣',
      'Psychological': '🟠',
      'Financial': '💰',
      'Neglect': '🟡',
      'Discriminatory': '⚫',
      'Institutional': '🏢',
    };
    return map[type] || '❓';
  };

  const getStatusColor = (status: string) => {
    const map: any = {
      'Assessment Pending': 'bg-amber-100 text-amber-800',
      'Section 42 Enquiry Opened': 'bg-blue-100 text-blue-800',
      'Section 42 Enquiry Closed': 'bg-green-100 text-green-800',
      'No Further Action (NFA)': 'bg-gray-100 text-gray-600',
    };
    return map[status] || 'bg-gray-100 text-gray-800';
  };

  const generateId = () => Date.now().toString(36) + Math.random().toString(36).substr(2, 5);

  const resetForm = () => {
    setFormData({
      resident_id: '',
      resident_name: '',
      type_of_alleged_abuse: 'Neglect',
      source_of_alert: 'Staff Report',
      supervising_council: 'Wakefield',
      section_42_referral_status: 'Assessment Pending',
      local_authority_case_reference: '',
      assigned_social_worker_info: '',
      date_alert_raised_internally: new Date().toISOString(),
      cqc_notification_submitted_at: null,
      cqc_notification_reference_num: null,
      leeds_msp_voice_recorded: false,
      kirklees_gateway_notification_date: null,
      kirklees_panel_review_meeting_confirmed: false,
      dsl_lead_signature: '',
      registered_manager_signoff_at: null,
      outcome_summary: '',
      is_sealed_and_locked: false,
      bodyMapAnnotations: [],
    });
    setBodyMapSelectedParts([]);
    setBodyMapAnnotations([]);
    setIsAdding(false);
    setIsEditing(false);
    setSelectedAlert(null);
    setActiveTab('records');
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAdd = () => {
    const newAlert: SafeguardingAlertRecord = {
      id: generateId(),
      resident_id: formData.resident_id || 'R001',
      resident_name: formData.resident_name || 'Unknown',
      type_of_alleged_abuse: formData.type_of_alleged_abuse as any || 'Neglect',
      source_of_alert: formData.source_of_alert as any || 'Staff Report',
      supervising_council: formData.supervising_council as any || 'Wakefield',
      section_42_referral_status: formData.section_42_referral_status as any || 'Assessment Pending',
      local_authority_case_reference: formData.local_authority_case_reference || '',
      assigned_social_worker_info: formData.assigned_social_worker_info || '',
      date_alert_raised_internally: formData.date_alert_raised_internally || new Date().toISOString(),
      cqc_notification_submitted_at: null,
      cqc_notification_reference_num: null,
      leeds_msp_voice_recorded: formData.leeds_msp_voice_recorded || false,
      kirklees_gateway_notification_date: null,
      kirklees_panel_review_meeting_confirmed: false,
      dsl_lead_signature: formData.dsl_lead_signature || '',
      registered_manager_signoff_at: null,
      outcome_summary: formData.outcome_summary || '',
      is_sealed_and_locked: false,
      bodyMapAnnotations: bodyMapAnnotations,
    };
    saveAlerts([newAlert, ...alerts]);
    setMessage({ text: '✅ Safeguarding alert raised successfully', type: 'success' });
    setTimeout(() => setMessage(null), 3000);
    resetForm();
  };

  const handleUpdate = () => {
    if (!selectedAlert) return;
    const updated = alerts.map(a => {
      if (a.id === selectedAlert.id) {
        return { ...a, ...formData, bodyMapAnnotations: bodyMapAnnotations };
      }
      return a;
    });
    saveAlerts(updated);
    setMessage({ text: '✅ Alert updated', type: 'success' });
    setTimeout(() => setMessage(null), 3000);
    resetForm();
  };

  const handleLock = (id: string) => {
    const alert = alerts.find(a => a.id === id);
    if (!alert) return;
    if (!alert.dsl_lead_signature) {
      setMessage({ text: '⚠️ DSL lead signature required before sealing', type: 'error' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    const updated = alerts.map(a => {
      if (a.id === id) {
        return {
          ...a,
          is_sealed_and_locked: true,
          registered_manager_signoff_at: new Date().toISOString(),
        };
      }
      return a;
    });
    saveAlerts(updated);
    setMessage({ text: '🔒 Alert sealed and locked. No further changes allowed.', type: 'success' });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleDelete = (id: string) => {
    if (!window.confirm('Permanently delete this safeguarding alert? This cannot be undone.')) return;
    const filtered = alerts.filter(a => a.id !== id);
    saveAlerts(filtered);
    setMessage({ text: '🗑️ Alert deleted', type: 'error' });
    setTimeout(() => setMessage(null), 2000);
  };

  const getCqcNotificationStatus = (alert: SafeguardingAlertRecord) => {
    if (alert.cqc_notification_reference_num) {
      return { status: 'completed', label: '✅ CQC Notification Filed', color: 'text-green-600' };
    }
    const raised = new Date(alert.date_alert_raised_internally);
    const now = new Date();
    const diffHours = (now.getTime() - raised.getTime()) / (1000 * 60 * 60);
    if (diffHours >= 24) {
      return { status: 'overdue', label: '🚨 Statutory Violation Hazard: CQC Regulation 13 / Notification 18 Exceeded. File immediate notification immediately.', color: 'text-red-600 animate-pulse' };
    }
    const remaining = Math.ceil(24 - diffHours);
    return { status: 'pending', label: `⏳ CQC Notification Required: ${remaining} hours remaining`, color: 'text-amber-600' };
  };

  const loadAlert = (alert: SafeguardingAlertRecord) => {
    setSelectedAlert(alert);
    setFormData({ ...alert });
    setBodyMapSelectedParts(alert.bodyMapAnnotations?.map(ann => ({ id: ann.id, name: `Pin at ${ann.x}%,${ann.y}%` })) || []);
    setBodyMapAnnotations(alert.bodyMapAnnotations || []);
    setIsEditing(true);
    setIsAdding(false);
    setActiveTab('body-map');
  };

  const saveAlerts = (data: SafeguardingAlertRecord[]) => {
    localStorage.setItem('rmacs_safeguarding_vault', JSON.stringify(data));
    setAlerts(data);
  };

  const filteredAlerts = alerts.filter(a => {
    if (filterCouncil !== 'All' && a.supervising_council !== filterCouncil) return false;
    if (filterStatus !== 'All' && a.section_42_referral_status !== filterStatus) return false;
    return true;
  });

  // ─── RENDER ──────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6 flex flex-wrap justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-[#123B36] flex items-center gap-2">
            <Shield className="w-6 h-6" /> Safeguarding Alert Registry
          </h2>
          <p className="text-[#6B7A76]">CQC Reg 13 & 17 – Multi‑agency adult safeguarding and statutory CQC notifications</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => { resetForm(); setIsAdding(true); setFormData(prev => ({ ...prev, date_alert_raised_internally: new Date().toISOString() })); }}
            className="bg-[#123B36] text-white px-4 py-2 rounded-lg hover:bg-[#0E2C28] flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> New Alert
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[#E1DCCD] pb-2">
        <button onClick={() => setActiveTab('records')} className={`px-4 py-2 rounded-lg text-sm font-semibold ${activeTab === 'records' ? 'bg-[#123B36] text-white' : 'bg-[#F7F5EE] text-[#123B36]'}`}>
          <FileText className="w-4 h-4 inline" /> Records
        </button>
        <button onClick={() => setActiveTab('body-map')} className={`px-4 py-2 rounded-lg text-sm font-semibold ${activeTab === 'body-map' ? 'bg-[#123B36] text-white' : 'bg-[#F7F5EE] text-[#123B36]'}`}>
          <Activity className="w-4 h-4 inline" /> Body Map
        </button>
      </div>

      {message && (
        <div className={`p-3 rounded-xl text-sm ${message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : message.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' : 'bg-blue-50 text-blue-800 border border-blue-200'}`}>
          {message.text}
        </div>
      )}

      {/* ─── BODY MAP TAB ───────────────────────────────────── */}
      {activeTab === 'body-map' && (
        <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6">
          <h3 className="text-lg font-bold text-[#123B36] mb-4">Interactive Body Map</h3>
          <p className="text-sm text-gray-500 mb-4">Click on any body part to add clinical notes, photos, or videos.</p>
          <div className="flex gap-2 mb-4 flex-wrap">
            <button className={`px-3 py-1 rounded ${bodyMapSide === 'front' ? 'bg-[#123B36] text-white' : 'bg-gray-200'}`} onClick={() => setBodyMapSide('front')}>Front</button>
            <button className={`px-3 py-1 rounded ${bodyMapSide === 'back' ? 'bg-[#123B36] text-white' : 'bg-gray-200'}`} onClick={() => setBodyMapSide('back')}>Back</button>
            <button className={`px-3 py-1 rounded ${bodyMapSide === 'left' ? 'bg-[#123B36] text-white' : 'bg-gray-200'}`} onClick={() => setBodyMapSide('left')}>Left</button>
            <button className={`px-3 py-1 rounded ${bodyMapSide === 'right' ? 'bg-[#123B36] text-white' : 'bg-gray-200'}`} onClick={() => setBodyMapSide('right')}>Right</button>
          </div>
          <BodyMap
            selectedParts={bodyMapSelectedParts}
            onPartSelect={(part) => setBodyMapSelectedParts(prev => [...prev, part])}
            onPartDeselect={(part) => setBodyMapSelectedParts(prev => prev.filter(p => p.id !== part.id))}
            annotations={bodyMapAnnotations}
            onAnnotationsChange={setBodyMapAnnotations}
          />
          <div className="mt-4 p-3 bg-[#F7F5EE] rounded-lg text-sm text-gray-600">
            <strong>Marked areas:</strong> {bodyMapSelectedParts.map(p => p.name).join(', ') || 'None'}
          </div>
        </div>
      )}

      {/* ─── RECORDS TAB ───────────────────────────────────── */}
      {activeTab === 'records' && (
        <>
          <div className="bg-white rounded-xl border border-[#E1DCCD] p-4 flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium">Filter by Council:</span>
              <select value={filterCouncil} onChange={(e) => setFilterCouncil(e.target.value)} className="border rounded px-2 py-1 text-sm">
                <option value="All">All</option>
                <option value="Wakefield">Wakefield</option>
                <option value="Leeds">Leeds</option>
                <option value="Bradford">Bradford</option>
                <option value="Kirklees">Kirklees</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Status:</span>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="border rounded px-2 py-1 text-sm">
                <option value="All">All</option>
                <option value="Assessment Pending">Assessment Pending</option>
                <option value="Section 42 Enquiry Opened">Section 42 Enquiry Opened</option>
                <option value="Section 42 Enquiry Closed">Section 42 Enquiry Closed</option>
                <option value="No Further Action (NFA)">No Further Action (NFA)</option>
              </select>
            </div>
          </div>

          {(isAdding || isEditing) && (
            <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6">
              <h3 className="text-lg font-bold text-[#123B36] mb-4">{isAdding ? 'New Safeguarding Alert' : 'Edit Alert'}</h3>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700">Resident ID</label>
                  <VoiceInput name="resident_id" value={formData.resident_id || ''} onChange={handleFormChange} className="w-full border rounded px-3 py-2" /></div>
                <div><label className="block text-sm font-medium text-gray-700">Resident Name</label>
                  <VoiceInput name="resident_name" value={formData.resident_name || ''} onChange={handleFormChange} className="w-full border rounded px-3 py-2" /></div>
                <div><label className="block text-sm font-medium text-gray-700">Type of Alleged Abuse</label>
                  <select name="type_of_alleged_abuse" value={formData.type_of_alleged_abuse || 'Neglect'} onChange={handleFormChange} className="w-full border rounded px-3 py-2">
                    <option value="Physical">Physical</option>
                    <option value="Sexual">Sexual</option>
                    <option value="Psychological">Psychological</option>
                    <option value="Financial">Financial</option>
                    <option value="Neglect">Neglect</option>
                    <option value="Discriminatory">Discriminatory</option>
                    <option value="Institutional">Institutional</option>
                  </select>
                </div>
                <div><label className="block text-sm font-medium text-gray-700">Source of Alert</label>
                  <select name="source_of_alert" value={formData.source_of_alert || 'Staff Report'} onChange={handleFormChange} className="w-full border rounded px-3 py-2">
                    <option value="Staff Report">Staff Report</option>
                    <option value="Family Complaint">Family Complaint</option>
                    <option value="Whistleblower">Whistleblower</option>
                    <option value="External Professional">External Professional</option>
                  </select>
                </div>
                <div><label className="block text-sm font-medium text-gray-700">Supervising Council</label>
                  <select name="supervising_council" value={formData.supervising_council || 'Wakefield'} onChange={handleFormChange} className="w-full border rounded px-3 py-2">
                    <option value="Wakefield">Wakefield</option>
                    <option value="Leeds">Leeds</option>
                    <option value="Bradford">Bradford</option>
                    <option value="Kirklees">Kirklees</option>
                  </select>
                </div>
                <div><label className="block text-sm font-medium text-gray-700">Section 42 Referral Status</label>
                  <select name="section_42_referral_status" value={formData.section_42_referral_status || 'Assessment Pending'} onChange={handleFormChange} className="w-full border rounded px-3 py-2">
                    <option value="Assessment Pending">Assessment Pending</option>
                    <option value="Section 42 Enquiry Opened">Section 42 Enquiry Opened</option>
                    <option value="Section 42 Enquiry Closed">Section 42 Enquiry Closed</option>
                    <option value="No Further Action (NFA)">No Further Action (NFA)</option>
                  </select>
                </div>
                <div><label className="block text-sm font-medium text-gray-700">Local Authority Case Reference</label>
                  <VoiceInput name="local_authority_case_reference" value={formData.local_authority_case_reference || ''} onChange={handleFormChange} className="w-full border rounded px-3 py-2" /></div>
                <div><label className="block text-sm font-medium text-gray-700">Assigned Social Worker Info</label>
                  <VoiceInput name="assigned_social_worker_info" value={formData.assigned_social_worker_info || ''} onChange={handleFormChange} className="w-full border rounded px-3 py-2" /></div>
                <div><label className="block text-sm font-medium text-gray-700">Date Alert Raised Internally</label>
                  <input type="datetime-local" name="date_alert_raised_internally" value={formData.date_alert_raised_internally || ''} onChange={handleFormChange} className="w-full border rounded px-3 py-2" /></div>
                <div><label className="block text-sm font-medium text-gray-700">CQC Notification Submitted At</label>
                  <VoiceInput name="cqc_notification_submitted_at" value={formData.cqc_notification_submitted_at || ''} onChange={handleFormChange} className="w-full border rounded px-3 py-2" placeholder="YYYY-MM-DDTHH:MM" /></div>
                <div><label className="block text-sm font-medium text-gray-700">CQC Notification Reference Number</label>
                  <VoiceInput name="cqc_notification_reference_num" value={formData.cqc_notification_reference_num || ''} onChange={handleFormChange} className="w-full border rounded px-3 py-2" /></div>

                {formData.supervising_council === 'Leeds' && (
                  <div className="col-span-2 border border-blue-200 bg-blue-50 p-3 rounded-lg">
                    <label className="flex items-center gap-2">
                      <input type="checkbox" name="leeds_msp_voice_recorded" checked={formData.leeds_msp_voice_recorded || false} onChange={handleFormChange} className="w-4 h-4" />
                      <span className="text-sm font-medium text-blue-800">Making Safeguarding Personal (MSP): Was the person's voice and desired outcome recorded at the start of the inquiry?</span>
                    </label>
                  </div>
                )}

                {formData.supervising_council === 'Kirklees' && (
                  <div className="col-span-2 border border-purple-200 bg-purple-50 p-3 rounded-lg">
                    <p className="text-sm font-medium text-purple-800">Kirklees Council Safeguarding Interventions</p>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <div>
                        <label className="block text-xs text-gray-600">Kirklees Gateway Team Notification Date</label>
                        <input type="date" name="kirklees_gateway_notification_date" value={formData.kirklees_gateway_notification_date || ''} onChange={handleFormChange} className="border rounded px-2 py-1 w-full text-sm" />
                      </div>
                      <div className="flex items-end">
                        <label className="flex items-center gap-2 text-sm">
                          <input type="checkbox" name="kirklees_panel_review_meeting_confirmed" checked={formData.kirklees_panel_review_meeting_confirmed || false} onChange={handleFormChange} className="w-4 h-4" />
                          Panel Review Meeting Confirmed
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700">DSL Lead Signature (Name)</label>
                  <VoiceInput name="dsl_lead_signature" value={formData.dsl_lead_signature || ''} onChange={handleFormChange} className="w-full border rounded px-3 py-2" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Outcome Summary</label>
                  <VoiceTextarea name="outcome_summary" value={formData.outcome_summary || ''} onChange={handleFormChange} className="w-full border rounded px-3 py-2" rows={3} />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={isAdding ? handleAdd : handleUpdate} className="bg-[#123B36] text-white px-6 py-2 rounded-lg hover:bg-[#0E2C28]">
                  {isAdding ? 'Raise Alert' : 'Update'}
                </button>
                <button onClick={resetForm} className="bg-gray-200 px-6 py-2 rounded-lg hover:bg-gray-300">Cancel</button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-[#E1DCCD] overflow-hidden">
            <div className="bg-[#F7F5EE] px-6 py-3 border-b border-[#E1DCCD] flex justify-between items-center">
              <h3 className="font-bold text-[#123B36]">Active Safeguarding Alerts</h3>
              <span className="text-sm text-gray-500">{filteredAlerts.length} records</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F7F5EE]">
                  <tr>
                    <th className="p-3 text-left">Resident</th>
                    <th className="p-3 text-left">Abuse Type</th>
                    <th className="p-3 text-left">Council</th>
                    <th className="p-3 text-left">Status</th>
                    <th className="p-3 text-left">CQC Notification</th>
                    <th className="p-3 text-left">Locked</th>
                    <th className="p-3 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAlerts.length === 0 ? (
                    <tr><td colSpan={7} className="p-6 text-center text-gray-400">No safeguarding alerts</td></tr>
                  ) : (
                    filteredAlerts.map(a => {
                      const cqcStatus = getCqcNotificationStatus(a);
                      return (
                        <tr key={a.id} className={`border-b border-[#E1DCCD] hover:bg-[#F7F5EE] transition ${cqcStatus.status === 'overdue' ? 'bg-red-50 border-red-300' : ''}`}>
                          <td className="p-3">
                            <div>
                              <span className="font-medium">{a.resident_name}</span>
                              <span className="text-xs text-gray-400 ml-1">({a.resident_id})</span>
                            </div>
                          </td>
                          <td className="p-3">{a.type_of_alleged_abuse}</td>
                          <td className="p-3">{a.supervising_council}</td>
                          <td className="p-3">{a.section_42_referral_status}</td>
                          <td className="p-3">
                            <span className={`text-xs font-medium ${cqcStatus.color}`}>
                              {cqcStatus.label}
                            </span>
                          </td>
                          <td className="p-3">
                            {a.is_sealed_and_locked ? <Lock className="w-4 h-4 text-green-600" /> : <Unlock className="w-4 h-4 text-gray-400" />}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              {!a.is_sealed_and_locked && (
                                <>
                                  <button onClick={() => loadAlert(a)} className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-xs">
                                    <Edit className="w-3 h-3" /> Edit
                                  </button>
                                  <button onClick={() => handleLock(a.id)} className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 text-xs">
                                    <Lock className="w-3 h-3" /> Lock
                                  </button>
                                  <button onClick={() => handleDelete(a.id)} className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 text-xs">
                                    <Trash2 className="w-3 h-3" /> Delete
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default SafeguardingModule;